import { Schema } from 'effect';
import { decodeSegment } from './shared';

/**
 * One thing the store offers, as a picker lists it.
 *
 * The same shape whichever collection it came from, which is the engine's arrangement rather than a simplification of ours: a construction model, a profile set, a surface treatment and a glass spacer are all answered as a name and a path, from endpoints that take the same query and wrap the answer in the same
 * envelope. So one type covers them, and one call fetches them — see `services/search.ts`.
 *
 * Here rather than beside the call that fetches it, because it is not wire shape: a picker holds it, tests construct it by hand, and it carries a `key` the API never sends as a field. What the API wraps it in — the collection envelope, the response body — stays in the service and unexported, because nothing holds
 * those past the call that decoded them.
 */
export class SearchResult extends Schema.Class<SearchResult>('SearchResult')(
	Schema.Struct({
		name: Schema.NonEmptyString,
		self: Schema.NonEmptyString,
	}),
) {
	/**
	 * The code the engine stores this under, and what refers to it everywhere else.
	 *
	 * The API only spells it out as the last segment of `self` — `.../construction-models/ABH1` is the model `ABH1`, and `.../component-surface-treatments/9010` the treatment `9010`. Derived here so no caller slices the path itself.
	 */
	public get key(): string {
		return this.self.slice(this.self.lastIndexOf('/') + 1);
	}

	/**
	 * The code as a user reads it — `0TS11MODULMÅL` where the path says `0TS11MODULM%C3%85L` — which is what it is shown as.
	 *
	 * Decoded from {@link SearchResult.key}, which stays as the engine wrote it: it is what a path is built from — see `decodeSegment`.
	 */
	public get label(): string {
		return decodeSegment(this.key);
	}

	/** Whether `text` is this one's code, as written or as read: a user typing a key types what they see, which is the label. */
	public isKeyedBy(text: string): boolean {
		return text === this.key || text === this.label;
	}
}
